import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormBuilder,
  Validators,
} from "@angular/forms";

import { AuthService } from "./../auth.service";
import { AppSettingsService } from "./../../shared/services/app-settings.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-reset-password",
  templateUrl: "./reset-password.component.html",
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  passwordForm: FormGroup;
  formError: string;
  resetPasswordSuccess: boolean = false;
  private destroy$ = new Subject<void>();

  private formValues = {
    password: "",
    confirmation: "",
  };

  private hiddenValues = {
    token: "",
    email: "",
  };

  formMessages = {
    password: "",
    confirmation: "",
  };

  private validationMessages = {
    password: {
      required: "Password is required.",
      passwordComplexity:
        "Password must meet complexity requirements (one number, one uppercase letter, one symbol, 8 characters).",
    },
    confirmation: {
      required: "Password Confirmation is required.",
      validateEqual: "Passwords do not match.",
    },
    form: {
      request: "Reset password request failed. The token may be expired.",
      token: "Invalid reset password request.",
      email: "Invalid reset password request.",
    },
  };

  constructor(
    private appSettings: AppSettingsService,
    private authService: AuthService,
    private viewState: ViewStateService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {}

  ngOnInit() {
    this.setHiddenValues();
    this.buildForm();
  }

  onSubmit(updatedFormValue: any) {
    this.viewState.startLoading();

    this.formValues = updatedFormValue;
    this.validateForm();
    this.handleFormResult();
  }

  private setHiddenValues() {
    this.hiddenValues.email =
      this.router.routerState.snapshot.root.queryParams["email"];
    this.hiddenValues.token =
      this.router.routerState.snapshot.root.queryParams["token"];
  }

  private handleFormResult() {
    this.formError = "";

    if (this.passwordForm.valid) {
      if (!this.hiddenValues.email) {
        this.formError = this.validationMessages.form.email;
        this.viewState.stopLoading();
        return;
      }

      if (!this.hiddenValues.token) {
        this.formError = this.validationMessages.form.token;
        this.viewState.stopLoading();
        return;
      }

      this.authService
        .resetPassword(
          this.hiddenValues.email,
          this.hiddenValues.token,
          this.formValues.password
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (success: boolean) => {
            this.viewState.stopLoading();
            this.resetPasswordSuccess = true;
          },
          (e) => {
            this.formError = this.validationMessages.form.request;
            this.viewState.stopLoading();
          }
        );
    }
  }

  private buildForm() {
    this.passwordForm = this.formBuilder.group({
      password: [
        this.formValues.password,
        [Validators.required, this.passwordComplexity],
      ],
      confirmation: [this.formValues.confirmation, [Validators.required]],
    });
  }

  private validateForm() {
    if (!this.passwordForm) {
      return;
    }

    for (const control in this.passwordForm.controls)
      this.passwordForm.controls[control].updateValueAndValidity();

    var hasErrors = false;

    for (const field in this.formMessages) {
      // clear previous error message (if any)
      this.formMessages[field] = "";
      const control = this.passwordForm.get(field);
      if (control && !control.valid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formMessages[field] += messages[key] + " ";
        }
      }
    }

    if (hasErrors) this.viewState.stopLoading();
  }

  private passwordComplexity(c: FormControl) {
    if (!c.value) return null;

    var valid = true;
    if (!c.value.match(/\d/)) valid = false;
    if (!c.value.match(/[A-Z]/)) valid = false;
    if (!c.value.match(/[a-z]/)) valid = false;
    if (!c.value.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/)) valid = false;
    if (!c.value.match(/^.{8,255}$/)) valid = false;

    if (valid) return null;

    return { passwordComplexity: { valid: "invalid" } };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
